import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Landmark, ChevronRight, Loader2, HeartPulse, CalendarDays, Clock } from "lucide-react";
import parentApi from "@/lib/parentApi";
import { fmtT, fmtDur, ENTRY_ICON, VitalsChips } from "@/components/care/ClientStayDialog";

const fmtD = (v) => new Date(v).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

function Stay({ s }) {
  const entries = s.entries || [];
  return (
    <div className={`rounded-2xl bg-white shadow-sm p-4 ${!s.departed_at ? "ring-2 ring-emerald-300" : ""}`} data-testid={`family-stay-${s.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-semibold text-slate-900">{fmtD(s.arrived_at)}</span>
        <span className="text-slate-600">{fmtT(s.arrived_at)} → {s.departed_at ? fmtT(s.departed_at) : <b className="text-emerald-700">on site now</b>}</span>
        <span className="ml-auto font-display font-bold text-slate-900">{fmtDur(s.duration_minutes ?? s.minutes_so_far)}</span>
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">
        Brought in by {s.dropoff_name || "—"}{s.departed_at && <> · picked up by {s.pickup_name || "—"}</>}{s.arrival_note && <> · {s.arrival_note}</>}{s.departure_note && <> · {s.departure_note}</>}
      </div>
      {entries.length > 0 && (
        <ul className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
          {entries.map((e, i) => { const I = ENTRY_ICON[e.type] || Clock; return (
            <li key={i} className="flex items-start gap-2 text-xs" data-testid={`family-entry-${s.id}-${i}`}>
              <I className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
              <span className="text-slate-500 w-14 shrink-0">{fmtT(e.at)}</span>
              <span className="min-w-0 flex-1 text-slate-800">
                <span className="font-medium capitalize">{e.type}</span>
                {e.type === "vitals" ? <div className="mt-0.5"><VitalsChips data={e.data || {}} note={e.text} /></div> : e.text ? <> — {e.text}</> : null}
                {e.by_name && <span className="text-slate-400"> · {e.by_name}</span>}
              </span>
            </li>
          ); })}
        </ul>
      )}
    </div>
  );
}

export default function ParentClientPage() {
  const { clientId } = useParams();
  const nav = useNavigate();
  const { schools } = useOutletContext();
  const [stays, setStays] = useState(null);

  const { facility, client } = useMemo(() => {
    for (const sc of schools || []) {
      const c = (sc.clients || []).find((x) => x.id === clientId);
      if (c) return { facility: sc, client: c };
    }
    return {};
  }, [schools, clientId]);

  useEffect(() => {
    setStays(null);
    const load = () => parentApi.get(`/parent/clients/${clientId}/stays`).then(({ data }) => setStays(data)).catch(() => setStays([]));
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [clientId]);

  if (schools === null) return <div className="flex-1 flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!client) return <div className="p-8 text-center text-sm text-slate-500" data-testid="parent-client-missing">This person isn't linked to your account.</div>;

  const totalMin = (stays || []).reduce((a, s) => a + (s.duration_minutes || 0), 0);
  const vitals = (stays || []).flatMap((s) => (s.entries || []).filter((e) => e.type === "vitals").map((e) => ({ ...e, stay: s })));

  return (
    <div className="flex-1 flex flex-col" data-testid="parent-client-page">
      <header className="px-6 pt-2 pb-4">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm px-5 py-5">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 truncate" data-testid="parent-client-facility">{facility.name}</div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="parent-client-name">{client.full_name}</h1>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${client.on_site ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid="parent-client-status">
              <span className={`h-2 w-2 rounded-full ${client.on_site ? "bg-emerald-500" : "bg-slate-400"}`} />
              {client.on_site ? `On site since ${fmtT(client.arrived_at)}` : "Not on site"}
            </span>
          </div>
          {client.needs && <div className="text-xs text-slate-500 mt-1">{client.needs}</div>}
        </div>
      </header>

      <main className="flex-1 w-full px-6 pb-6 space-y-4">
        <button onClick={() => nav(`/parent/office/${facility.company_id}?client=${client.id}`)} data-testid="parent-client-office-link" className="w-full flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-sm text-left hover:shadow transition">
          <span className="h-9 w-9 shrink-0 rounded-xl inline-flex items-center justify-center bg-emerald-100 text-emerald-700"><Landmark className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-slate-900">Message the facility</span>
            <span className="block text-[11px] text-slate-500 truncate">{facility.name} staff</span>
          </span>
          {facility.office_unread > 0 && <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">{facility.office_unread}</span>}
          <ChevronRight className="h-4 w-4 text-slate-300" />
        </button>

        <div className="grid grid-cols-3 gap-2">
          {[[CalendarDays, "Stays (60 days)", stays?.length ?? "…"], [Clock, "Hours", stays ? fmtDur(totalMin) : "…"], [HeartPulse, "Vitals logged", stays ? vitals.length : "…"]].map(([I, l, v]) => (
            <div key={l} className="rounded-2xl bg-white shadow-sm p-3 text-center" data-testid={`parent-client-kpi-${l.split(" ")[0].toLowerCase()}`}>
              <I className="h-4 w-4 mx-auto text-primary" />
              <div className="font-display text-xl font-bold text-slate-900">{v}</div>
              <div className="text-[11px] text-slate-500">{l}</div>
            </div>
          ))}
        </div>

        {vitals.length > 0 && (
          <section className="rounded-2xl bg-white shadow-sm p-4" data-testid="parent-client-latest-vitals">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Latest vitals · {fmtD(vitals[0].at)} {fmtT(vitals[0].at)}</div>
            <VitalsChips data={vitals[0].data || {}} note={vitals[0].text} />
          </section>
        )}

        <section className="space-y-2" data-testid="parent-client-stays">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 px-1">Daily stays</div>
          {stays === null && <div className="rounded-2xl bg-white p-10 text-center shadow-sm"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>}
          {stays?.length === 0 && <div className="rounded-2xl bg-white p-8 text-center shadow-sm text-sm text-slate-500" data-testid="parent-client-stays-empty">No stays in the last 60 days.</div>}
          {stays?.map((s) => <Stay key={s.id} s={s} />)}
        </section>
      </main>
    </div>
  );
}
